import React, {useState} from 'react'
import {Helmet} from 'react-helmet-async'
import styled from 'styled-components'
import {EnvironmentPreferences} from '../../../common/types'
import {sanitizeRecord} from '../../../common/types/helpers'
import {Form} from '../../../components/form'
import {InputGroup} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {NoResultsPermissions} from '../../../components/library/list'
import {Switch} from '../../../components/library/toggles/switch'
import {Loader} from '../../../components/loader'
import {
  useGetEnvironmentQuery,
  useUpdateEnvironmentMutation
} from '../../../data/workbench-api/generated/environment'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'

const Wrapper = styled.div``

export const ShareLinkSettings = ({
  environment
}: {
  environment: EnvironmentNode
}) => {
  const [ready, setReady] = useState(false)
  const canView = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.View
  )
  const canEdit = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.Edit
  )
  const {data: query, loading} = useGetEnvironmentQuery({
    variables: {id: environment.id},
    onCompleted: data => {
      resetState(sanitizeRecord(data.environment?.preferences ?? {}))
      setReady(true)
    }
  })

  const [localObj, setLocalObj, dirty, resetState] =
    useStateChanged<EnvironmentPreferences>({})
  const [doMutation, {loading: saving, error}] = useUpdateEnvironmentMutation({
    onCompleted: data =>
      resetState(
        sanitizeRecord(data?.updateEnvironment?.environment?.preferences ?? {})
      ),
    refetchQueries: ['GetOrganisation']
  })

  const onReset = () => {
    resetState(sanitizeRecord(query?.environment?.preferences ?? {}))
  }
  return (
    <Section>
      <Helmet>
        <title>Share Link Settings</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="bell" /> Share Link Settings
        </IconHeading>
      </header>

      <SectionInfo>
        <p>Enable share links</p>
      </SectionInfo>

      <SectionContent>
        {!ready ? (
          <Loader />
        ) : canView ? (
          <Form
            onSubmit={async () => {
              await doMutation({
                variables: {
                  id: environment.id,
                  preferences: localObj
                }
              })
            }}
            onCancel={dirty ? onReset : undefined}
            submitText="Update"
            cancelText="Discard changes"
            error={error}
            isValid={dirty && !loading}
            loading={loading || saving}
          >
            <Wrapper>
              <InputGroup>
                <Switch
                  disabled={!canEdit}
                  label="Enable share links"
                  isForm={true}
                  appearance="light"
                  labelPosition="left"
                  checked={!!localObj.shareLinkConfiguration?.enableShareLink}
                  onChange={enableShareLink =>
                    setLocalObj(c => ({
                      ...c,
                      shareLinkConfiguration: {
                        enableShareLink
                      }
                    }))
                  }
                />
              </InputGroup>
            </Wrapper>
          </Form>
        ) : (
          <div style={{margin: '50px 0px'}}>
            <NoResultsPermissions message="Sorry, you don't have permission to view these settings" />
          </div>
        )}
      </SectionContent>
    </Section>
  )
}
